// JSON-LD schema.org builders — used by <SEO> component.
// Every builder returns a plain object ready for JSON.stringify().
//
// GEO/AEO principle: expose canonical, first-person authoritative data so
// Google, Bing, Perplexity, ChatGPT search and Google AI Overviews can quote
// Point Penta directly rather than aggregating rumours.

const SITE = process.env.REACT_APP_BACKEND_URL || "";

// --------------------------------------------------------------------------
// Organization / brand entity — extend with `sameAs` when social handles land.
// --------------------------------------------------------------------------
export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    "@id": `${SITE}/#organization`,
    name: "Point Penta Real Estate LLC",
    alternateName: "Point Penta",
    url: SITE,
    logo: `${SITE}/logo.png`,
    image: `${SITE}/logo.png`,
    description:
      "Point Penta Real Estate LLC is a boutique Dubai brokerage helping buyers, investors, landlords and tenants discover curated UAE property mandates. RERA-licensed advisors specialise in off-plan launches, residential resale and commercial leasing across every emirate.",
    address: {
      "@type": "PostalAddress",
      streetAddress: "902, Ithra Tower, Al Garhoud",
      addressLocality: "Dubai",
      addressRegion: "Dubai",
      addressCountry: "AE",
    },
    telephone: "+971-55-739-6664",
    email: "info@pointpenta.com",
    openingHoursSpecification: [{
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      opens: "09:00", closes: "19:00",
    }],
    areaServed: [
      { "@type": "City", name: "Dubai" },
      { "@type": "City", name: "Abu Dhabi" },
      { "@type": "City", name: "Sharjah" },
      { "@type": "City", name: "Ras Al Khaimah" },
      { "@type": "City", name: "Ajman" },
      { "@type": "City", name: "Fujairah" },
      { "@type": "City", name: "Umm Al Quwain" },
    ],
    knowsLanguage: ["English", "Arabic", "Russian", "Chinese", "French", "Japanese", "Korean"],
    priceRange: "AED 500,000 – AED 500,000,000",
    slogan: "Curated Dubai property mandates — 24-hour turnaround.",
    foundingLocation: { "@type": "Place", name: "Dubai, UAE" },
    sameAs: [
      "https://www.instagram.com/pointpenta.ae",
      "https://ae.linkedin.com/company/point-penta-real-estate-llc",
    ],
  };
}

// --------------------------------------------------------------------------
// WebSite entity — enables sitelinks searchbox in Google.
// --------------------------------------------------------------------------
export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE}/#website`,
    url: SITE,
    name: "Point Penta Real Estate",
    publisher: { "@id": `${SITE}/#organization` },
    inLanguage: ["en", "ar-AE", "ru", "zh-CN", "fr", "ja", "ko"],
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE}/properties?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}

// --------------------------------------------------------------------------
// Breadcrumbs
// --------------------------------------------------------------------------
export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE}${it.path}`,
    })),
  };
}

// --------------------------------------------------------------------------
// FAQ — the single highest-ROI schema for AEO. Feeds Google AI Overviews,
// Perplexity and ChatGPT search near-verbatim answers.
// --------------------------------------------------------------------------
export function faqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

// --------------------------------------------------------------------------
// Real-estate listing (residential / rental)
// --------------------------------------------------------------------------
export function listingSchema(p) {
  return {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    name: p.title,
    description: p.description?.slice(0, 500),
    url: `${SITE}/properties/${p.id}`,
    datePosted: p.created_at,
    image: (p.images || [])
      .slice(0, 4)
      .map((im) => (im?.startsWith?.("http") ? im : `${SITE}/api/files/${im}`)),
    address: {
      "@type": "PostalAddress",
      addressLocality: p.community,
      addressRegion: p.city || "Dubai",
      addressCountry: "AE",
      streetAddress: p.address || "",
    },
    geo:
      p.latitude && p.longitude
        ? { "@type": "GeoCoordinates", latitude: p.latitude, longitude: p.longitude }
        : undefined,
    numberOfBedrooms: p.bedrooms || undefined,
    numberOfBathroomsTotal: p.bathrooms || undefined,
    floorSize: p.area_sqft
      ? { "@type": "QuantitativeValue", value: p.area_sqft, unitCode: "FTK" }
      : undefined,
    offers: {
      "@type": "Offer",
      price: p.price,
      priceCurrency: p.currency || "AED",
      availability:
        p.status === "published"
          ? "https://schema.org/InStock"
          : "https://schema.org/SoldOut",
    },
  };
}

// --------------------------------------------------------------------------
// Place — for community / area pages. Anchors each community as a named
// entity so Google's Knowledge Graph and AI answer engines treat "Dubai
// Marina" as an entity Point Penta authoritatively describes.
// --------------------------------------------------------------------------
export function placeSchema(a) {
  const url = `${SITE}/areas/${a.slug}`;
  const schema = {
    "@context": "https://schema.org",
    "@type": "Place",
    "@id": `${url}#place`,
    name: a.name,
    alternateName: a.sub_community || undefined,
    description: a.description || a.intro || undefined,
    url,
    image: a.hero_image || undefined,
    address: {
      "@type": "PostalAddress",
      addressLocality: a.name,
      addressRegion: a.emirate || "Dubai",
      addressCountry: "AE",
    },
    containedInPlace: {
      "@type": "City",
      name: a.emirate || "Dubai",
      addressCountry: "AE",
    },
  };
  if (a.latitude && a.longitude) {
    schema.geo = {
      "@type": "GeoCoordinates",
      latitude: a.latitude,
      longitude: a.longitude,
    };
  }
  // Additional properties recognised by Google — surfaced in rich results.
  const additional = [];
  if (a.rental_yield) {
    additional.push({
      "@type": "PropertyValue",
      name: "Average rental yield",
      value: `${a.rental_yield}%`,
    });
  }
  if (a.investment_score) {
    additional.push({
      "@type": "PropertyValue",
      name: "Point Penta investment score",
      value: `${a.investment_score}/10`,
    });
  }
  if (a.average_price_apartment) {
    additional.push({
      "@type": "PropertyValue",
      name: "Average apartment price per sqft",
      value: `AED ${a.average_price_apartment}`,
    });
  }
  if (additional.length) schema.additionalProperty = additional;
  return schema;
}

// --------------------------------------------------------------------------
// Off-plan project → Product schema. Unlocks rich Google shopping-style
// panels and gives AI engines a clean structured offer to summarise.
// --------------------------------------------------------------------------
export function projectSchema(p) {
  const url = `${SITE}/projects/${p.slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": `${url}#project`,
    name: p.name,
    description: p.description || p.summary || undefined,
    url,
    image:
      (Array.isArray(p.images) && p.images[0]) ||
      p.hero_image ||
      undefined,
    brand: p.developer_name
      ? { "@type": "Brand", name: p.developer_name }
      : undefined,
    category: "Off-plan real estate",
    offers:
      p.starting_price
        ? {
            "@type": "Offer",
            price: p.starting_price,
            priceCurrency: p.currency || "AED",
            availability: "https://schema.org/PreOrder",
            url,
            seller: { "@id": `${SITE}/#organization` },
          }
        : undefined,
    additionalProperty: [
      p.handover_date && {
        "@type": "PropertyValue",
        name: "Handover",
        value: p.handover_date,
      },
      p.total_units && {
        "@type": "PropertyValue",
        name: "Total units",
        value: p.total_units,
      },
      p.community && {
        "@type": "PropertyValue",
        name: "Community",
        value: p.community,
      },
    ].filter(Boolean),
  };
}

// --------------------------------------------------------------------------
// Article — Journal posts. Standard NewsArticle-compatible for Google News.
// --------------------------------------------------------------------------
export function articleSchema(post) {
  const url = `${SITE}/journal/${post.slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${url}#article`,
    headline: post.title,
    description: post.summary || post.excerpt || undefined,
    image: post.hero_image || post.cover || undefined,
    datePublished: post.published_at || post.created_at,
    dateModified: post.updated_at || post.published_at || post.created_at,
    author: {
      "@type": "Organization",
      name: "Point Penta Editorial",
      url: SITE,
    },
    publisher: { "@id": `${SITE}/#organization` },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    articleSection: post.category || "Dubai Property Insights",
    inLanguage: post.language || "en",
    keywords: Array.isArray(post.tags) ? post.tags.join(", ") : undefined,
  };
}

// --------------------------------------------------------------------------
// ItemList — for "Top 5 investment communities" style widgets.
// AI engines love ranked lists.
// --------------------------------------------------------------------------
export function itemListSchema({ name, items }) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name,
    numberOfItems: items.length,
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      url: `${SITE}${it.path}`,
    })),
  };
}
